import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../utils/auth';
import LoadingIndicator from './LoadingIndicator';
import Modal, { DialogHeader, DialogFooter, DialogBody } from './ui/Modal';

const ProfileModal = ({ isOpen, onClose, user, userType }) => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [clinicianId, setClinicianId] = useState(null);

  const fetchProfileData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      
      if (userType === 'clinician') {
        // First, get clinician by user_id to get the clinicians_id
        const userResponse = await fetch(`${API_BASE_URL}/clinicians/user/${user.user_id}`, { headers });
        if (!userResponse.ok) throw new Error('Failed to fetch clinician by user ID');
        
        const userData = await userResponse.json();
        const clinician_id = userData.id; // This is the clinicians_id
        setClinicianId(clinician_id);
        
        // Now fetch full clinician data with relations using clinicians_id
        const response = await fetch(`${API_BASE_URL}/clinicians/${clinician_id}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch profile');
        
        const data = await response.json();
        // Format date for input field (YYYY-MM-DD)
        if (data.dob) {
          const dateObj = new Date(data.dob);
          if (!isNaN(dateObj.getTime())) {
            data.dob = dateObj.toISOString().split('T')[0];
          }
        }
        // Include username from user object
        if (data.user && data.user.username) {
          data.username = data.user.username;
        }
        setProfileData(data);
        setFormData(data);
      } else {
        // Admin endpoint
        const response = await fetch(`${API_BASE_URL}/admins/user/${user.user_id}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch profile');
        
        const data = await response.json();
        setProfileData(data);
        setFormData(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id, userType, API_BASE_URL]);

  useEffect(() => {
    if (isOpen && user?.user_id) {
      fetchProfileData();
    }
  }, [isOpen, user?.user_id, fetchProfileData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) return;

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (userType === 'clinician') {
        if (!clinicianId) {
          setError('Clinician ID not found');
          setLoading(false);
          return;
        }

        // Prepare update data - only include fields that can be updated
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          preferredName: formData.preferredName,
          dob: formData.dob,
          gender: formData.gender,
          department_id: formData.department_id,
          role: formData.role,
          speciality: formData.speciality,
          active: formData.active
        };

        // Update clinician record
        const response = await fetch(`${API_BASE_URL}/clinicians/${clinicianId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(updateData)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to update profile' }));
          throw new Error(errorData.detail || 'Failed to update profile');
        }

        const updatedData = await response.json();

        // Update username in user record if it changed
        if (formData.username && formData.username !== profileData.username && profileData.user) {
          const userUpdateResponse = await fetch(`${API_BASE_URL}/users/${profileData.user.id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ username: formData.username })
          });

          if (!userUpdateResponse.ok) {
            const errorData = await userUpdateResponse.json().catch(() => ({ detail: 'Failed to update username' }));
            throw new Error(errorData.detail || 'Failed to update username');
          }

          // Update the user object in the response
          const updatedUser = await userUpdateResponse.json();
          updatedData.user = updatedUser;
          updatedData.username = updatedUser.username;
        }

        setProfileData(updatedData);
        setFormData(updatedData);
      } else {
        // Admin update
        const response = await fetch(`${API_BASE_URL}/admins/${profileData.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to update profile' }));
          throw new Error(errorData.detail || 'Failed to update profile');
        }

        const updatedData = await response.json();
        setProfileData(updatedData);
        setFormData(updatedData);
      }

      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm('Are you sure you want to delete your profile? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) return;

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (userType === 'clinician') {
        if (!clinicianId) {
          setError('Clinician ID not found');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/clinicians/${clinicianId}`, {
          method: 'DELETE',
          headers
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to delete profile' }));
          throw new Error(errorData.detail || 'Failed to delete profile');
        }

        // Profile deleted, close modal and redirect
        onClose();
        window.location.href = '/';
      } else {
        // Admin delete (if endpoint exists)
        const response = await fetch(`${API_BASE_URL}/admins/${profileData.id}`, {
          method: 'DELETE',
          headers
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to delete profile' }));
          throw new Error(errorData.detail || 'Failed to delete profile');
        }

        onClose();
        window.location.href = '/';
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 shadow-2xl">
        <DialogHeader
          title="Profile"
          subtitle={userType === 'clinician' ? 'Clinician account' : 'Administrator account'}
          onClose={onClose}
          tone="teal"
        />

        <DialogBody className="p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {loading && !profileData ? (
            <LoadingIndicator message="Loading profile..." />
          ) : profileData ? (
            <>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.lastName}</p>
                  )}
                </div>

                {userType === 'clinician' && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.username || ''}
                          onChange={(e) => handleInputChange('username', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.username || profileData.user?.username || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth</label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={formData.dob || ''}
                          onChange={(e) => handleInputChange('dob', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.dob || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Gender</label>
                      {isEditing ? (
                        <select
                          value={formData.gender || ''}
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      ) : (
                        <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.gender || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.role || ''}
                          onChange={(e) => handleInputChange('role', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.role || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Speciality</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.speciality || ''}
                          onChange={(e) => handleInputChange('speciality', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.speciality || 'N/A'}</p>
                      )}
                    </div>

                    {profileData.user && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                        <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.user.email || 'N/A'}</p>
                      </div>
                    )}
                  </>
                )}

                {userType === 'admin' && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                      <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Admin ID</label>
                      <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">{profileData.adminId || profileData.id || 'N/A'}</p>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : null}
        </DialogBody>

        {profileData ? (
          <DialogFooter className="sm:justify-stretch">
            {!isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleDeleteProfile}
                  disabled={loading}
                  className="flex-1 bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : 'Delete Profile'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/change-password')}
                  className="flex-1 bg-white border border-gray-300 text-gray-900 px-6 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="flex-1 bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  Edit Profile
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData(profileData);
                    setError(null);
                  }}
                  className="flex-1 bg-white border border-gray-300 text-gray-900 px-6 py-2.5 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="flex-1 bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </DialogFooter>
        ) : null}
      </div>
    </Modal>
  );
};

export default ProfileModal;

